import axiosNextGen from '../../../config/axios-nextgen';

/**
 * Letter Verification Service
 * Handles patient letter lookup and preview for admin verification
 */

/**
 * Search for patients by name or MRN.
 * The server caps results at `limit` (default 25). When the returned array length
 * equals `limit` the caller should assume more results exist and prompt the user
 * to refine their search term — no pagination is needed in the dropdown.
 * @param {string} q - Search term (MRN, first name, or last name)
 * @param {number} [limit=25] - Maximum number of results to request from the server
 * @returns {Promise<Array>}
 */
export async function searchPatients(q, limit = 25) {
    const response = await axiosNextGen.get('/api/admin/letter-verification/search', { params: { q, limit } });
    return response.data;
}

/**
 * Get all letters (results + recalls) for a patient
 * @param {string} patientId - Patient GUID
 * @returns {Promise<Array>}
 */
export async function getPatientLetters(patientId) {
    const response = await axiosNextGen.get(`/api/admin/letter-verification/${patientId}/letters`);
    return response.data;
}

/**
 * Get a pre-signed S3 URL for a result letter PDF
 * @param {string} patientResultId
 * @returns {Promise<{url: string}>}
 */
export async function getResultLetterUrl(patientResultId) {
    const response = await axiosNextGen.get(`/api/admin/letter-verification/result-letter-url/${patientResultId}`);
    return response.data;
}

/**
 * Get a pre-signed S3 URL for a recall letter PDF
 * @param {string} patientRecallId
 * @returns {Promise<{url: string}>}
 */
export async function getRecallLetterUrl(patientRecallId) {
    const response = await axiosNextGen.get(`/api/admin/letter-verification/recall-letter-url/${patientRecallId}`);
    return response.data;
}

/**
 * Preview a result letter PDF (rendered in real time, no print history written)
 * @param {string} patientResultId
 * @param {string} [ianaTimezone]
 * @returns {Promise<Blob>}
 */
export async function previewResultLetter(patientResultId, ianaTimezone) {
    const timezone = ianaTimezone || Intl.DateTimeFormat().resolvedOptions().timeZone || 'America/New_York';
    const response = await axiosNextGen.get(`/api/admin/letter-verification/result-preview/${patientResultId}`, {
        params: { ianaTimezone: timezone },
        responseType: 'blob'
    });
    return response.data;
}

/**
 * Preview a recall letter PDF (rendered in real time, no print history written)
 * @param {string} patientRecallId
 * @returns {Promise<Blob>}
 */
export async function previewRecallLetter(patientRecallId) {
    const response = await axiosNextGen.get(`/api/admin/letter-verification/recall-preview/${patientRecallId}`, {
        responseType: 'blob'
    });
    return response.data;
}
